import React, { useEffect, useState, useRef } from 'react';
import EmailRoundedIcon from '@material-ui/icons/EmailRounded';
import {
  ApolloClient, ApolloLink, HttpLink, InMemoryCache, useMutation,
} from '@apollo/client';
import { TOKEN_AUTH, TokenAuthRes, TokenAuthVars } from 'mutation/authentication';
import CLIENT_SETTINGS from 'lib/client_settings';
import jwt_decode, { JwtPayload } from 'jwt-decode';
import {
  Box, FormHelperText, IconButton, InputAdornment, Typography,
  TextField, TextFieldProps,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import FormControl from '@material-ui/core/FormControl';
import { useSnackbar } from 'notistack';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import XIcon from '@images/IconSidebar/X.svg';
import GpexeIcon from '@images/IconSidebar/gpexe.svg';
import UserIcon from '@images/icons/svg/userName.svg';
import PasswordIcon from '@images/icons/svg/padlock.svg';
import VisibilityIcon from '@images/icons/svg/visibility.svg';
import NoVisibilityIcon from '@images/icons/svg/noVisibility.svg';
import bg1 from '@images/slideshow/bg_login_1.webp';
import bg2 from '@images/slideshow/bg_login_2.webp';
import bg3 from '@images/slideshow/bg_login_3.webp';
import bg4 from '@images/slideshow/bg_login_4.webp';
import bg5 from '@images/slideshow/bg_login_5.webp';
import client_settings from 'lib/client_settings';
import BaseButton from './components/form/BaseButton';
import { validateEmail } from './components/utils/form';
import { UPDATE_USER_PASSWORD, UpdateUserPasswordRes, UpdateUserPasswordVars } from './query/user';
import { RESET_USER_PASSWORD, ResetUserPasswordRes, ResetUserPasswordVars } from './mutation/user';
import { APP_STATES, AppState } from './lib/global';

const LoginTextField: React.FC<TextFieldProps> = (props) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  return (
    <TextField
      {...props}
      variant="filled"
      size="small"
      label={props.label}
      className="autofill-fix"
      inputRef={inputRef}
      focused
      InputLabelProps={{
        ...props.InputLabelProps,
        required: false,
        shrink: true,
        style: {
          color: '#ffffff',
          fontSize: '0.875rem',
          overflow: 'hidden',
          lineHeight: 1,
          textAlign: 'left',
          position: 'absolute',
          right: 'auto',
          top: '-22px',
          transform: 'none',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(6px)',
          borderRadius: '4px',
          marginLeft: '-6px',
          paddingInline: '6px',
          zIndex: 9999,
          transition: 'top 0.3s ease, transform 0.3s ease, background-color 0.3s ease, padding-inline 0.3s ease, margin-left 0.3s ease',
          pointerEvents: 'none',
          mixBlendMode: 'normal',
          isolation: 'isolate',
          ...(props.InputLabelProps?.style || {}),
        },
      }}
      InputProps={{
        ...props.InputProps,
        disableUnderline: true,
        style: {
          color: '#ffffff',
          fontSize: '1rem',
          backgroundColor: 'transparent',
          textAlign: 'left',
          padding: '0',
          minHeight: 10,
          ...(props.InputProps?.style || {}),
        },
        classes: {
          ...(props.InputProps?.classes || {}),
          input: 'autofill-fix',
        },
      }}
      inputProps={{
        ...props.inputProps,
        'aria-label': props.label,
        style: {
          color: '#ffffff',
          fontSize: '1rem',
          verticalAlign: 'center',
          fontFamily: 'var(--font-family-alt)',
          fontWeight: 700,
          padding: '1px 0 1px 0',
          ...(props.inputProps?.style || {}),
        },
      }}
    />
  );
};

const BoxProperties = {
  mt: 1.5,
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
};

export type IJWTToken = JwtPayload & {
  email?: string;
};

const imageStyle: React.CSSProperties = {
  minHeight: '100vh',
  minWidth: '1024px',
  width: '100%',
  height: 'auto',
  position: 'fixed',
  top: 0,
  left: 0,
  objectFit: 'cover',
  transition: 'opacity 1s ease-in-out', // add smooth fade
};

const backgroundImages = [bg3, bg2, bg1, bg5, bg4];

export const getTokenDataFromLocalStorage = () => localStorage.getItem('exelio_token')
    && jwt_decode<IJWTToken>(localStorage.getItem('exelio_token'));

export const resetTranslationsFallback = (key: string) => {
  const blockedArr = key.split('-');
  if (blockedArr[0] === 'blockedFor') {
    const timeLeft = parseInt(blockedArr[1], 10);
    return `Too many attempts. Try again in ${timeLeft} minutes`;
  }
  const translations = {
    almostReady: 'Too many attempts. Try again in less than a minute',
    MinimumLength: 'Password is too short',
    UserAttributeSimilarity: 'Password is too similar to other user attributes',
    tokenError: 'This form is no longer available. Request the password reset one more time.',
    userInactive: 'This user is inactive!',
    unknownError: 'An error occurred while sending email',
    invalidForm: 'Form invalid',
  };

  if (Object.keys(translations).includes(key)) {
    return translations[key];
  }

  return key;
};

const handleLinkHover = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.currentTarget.style.textDecoration = 'underline';
  e.currentTarget.style.color = '#00C9B7';
};

const handleLinkOut = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.currentTarget.style.textDecoration = 'none';
  e.currentTarget.style.color = '#ffffff';
};

export default function Login() {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [section, setSection] = useState<'login' | 'recover' | 'reset'>(window.location.pathname.match(/^\/reset/) ? 'reset' : 'login');
  const [username, setUsername] = useState('');
  const [dialogMessage, setDialogMessage] = useState<false | string>(false);
  const [recoverUsername, setRecoverUsername] = useState('');
  const [resetPassword1, setResetPassword1] = useState('');
  const [resetPassword2, setResetPassword2] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<{[k: string]: string}>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const intervalRef = useRef<any>(null);
  const notificationRef = React.useRef<any>();
  const isMounted = React.useRef(false);
  const [animateIn, setAnimateIn] = useState(false);

  const WebsiteInfo = () => (
    <Box
      className="login-form"
      fontSize="0.875rem"
      color="#fff"
      fontFamily="var(--font-family-main)"
      style={{
        position: 'absolute',
        bottom: '45px',
        left: '65.5px',
      }}
    >
      <a
        href="https://www.gpexe.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="login-form__links"
        style={{
          fontSize: '0.875rem',
          color: '#ffffff',
          textDecoration: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-family-main)',
        }}
        onMouseOver={handleLinkHover}
        onMouseOut={handleLinkOut}
      >
        gpexe.com
      </a>
    </Box>
  );

  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setTimeout(() => {
        setCurrentIndex(nextIndex); // switch image
        setNextIndex((nextIndex + 1) % backgroundImages.length);
      }, 1000);
    }, 5000);

    return () => clearInterval(intervalRef.current);
  }, [nextIndex]);

  useEffect(() => {
    setInvalidFields((fields) => ({
      ...fields,
      resetPassword1: '',
      resetPassword2: resetPassword2 !== '' && resetPassword1 !== resetPassword2
        ? t('login.resetPassword.notEqual', 'Passwords are different')
        : '',
    }));
  }, [resetPassword1, resetPassword2]);

  useEffect(() => {
    /* USED TO AUTOMATICALLY AUTHENTICATE USER FROM OLD UI */
    // Retrieve the jwt_token from cookies and set it in localStorage
    const jwtToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('jwt_token='))
      ?.split('=')[1];

    if (jwtToken) {
      localStorage.setItem('exelio_token', jwtToken);
      const refreshJwtToken = localStorage.getItem('exelio_refresh_token');
      setTimeout(() => {
        // Delete the cookie
        document.cookie = 'jwt_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        // temporarily solution, then the entire code block will be removed;
        if (refreshJwtToken) {
          window.location.replace('/');
        }
      }, 250);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const [updateUserPassword] = useMutation<UpdateUserPasswordRes, UpdateUserPasswordVars>(UPDATE_USER_PASSWORD, {
    onError: (error) => {
      console.error(error);
      setIsSending(false);
      enqueueSnackbar(
        t('login.resetPassword.error', 'Error resetting your password, please try later.'),
        {
          variant: 'error',
          autoHideDuration: 5000,
        },
      );
    },
    onCompleted: (data) => {
      if (data.updateUserPassword.success) {
        enqueueSnackbar(
          t('user.resetPassword.resetSuccessful', 'Password created, please now login with your new credentials'),
          {
            variant: 'success',
            autoHideDuration: 5000,
          },
        );
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      } else {
        setIsSending(false);
        enqueueSnackbar(
          <div>
            <h3>{t('login.resetPassword.errors', 'Error updating password')}</h3>
            <br />
            <ul>
              {data.updateUserPassword.messageKeys.map((errorKey) => (
                <li>
                  {' '}
                  -
                  {' '}
                  {t(`user.resetPassword.errorMessage.${errorKey}`, resetTranslationsFallback(errorKey))}
                </li>
              ))}
            </ul>
          </div>,
          {
            variant: 'error',
            autoHideDuration: 5000,
            title: 'Error',
          },
        );
      }
    },
  });

  const [requestResetUserPasswordMut] = useMutation<ResetUserPasswordRes, ResetUserPasswordVars>(RESET_USER_PASSWORD, {
    onError: (error) => {
      console.error(error);
      setIsSending(false);
      enqueueSnackbar(
        t('login.resetPassword.error', 'Error resetting your password, please try later.'),
        {
          variant: 'error',
          autoHideDuration: 5000,
        },
      );
    },
    onCompleted: (data) => {
      setIsSending(false);
      if (data.requestResetUserPassword.success) {
        enqueueSnackbar(
          t('login.recoverPassword.emailSent', 'Request sent! Check your inbox.'),
          {
            variant: 'success',
            autoHideDuration: 5000,
          },
        );
      } else {
        enqueueSnackbar(
          <div>
            <h3>{t('login.requestResetPassword.errors', 'Error requesting password reset')}</h3>
            <br />
            <ul>
              {data.requestResetUserPassword.messageKeys.map((errorKey) => (
                <li>
                  {' '}
                  -
                  {' '}
                  {t(`user.resetPassword.errorMessage.${errorKey}`, resetTranslationsFallback(errorKey))}
                </li>
              ))}
            </ul>
          </div>,
          {
            variant: 'error',
            autoHideDuration: 5000,
            title: 'Error',
          },
        );
      }
    },
  });

  const login = async () => {
    setIsSending(true);

    const client = new ApolloClient({
      cache: new InMemoryCache(),
      link: ApolloLink.from([
        onError(({
          graphQLErrors, networkError, operation, forward,
        }) => {
          if (notificationRef.current) {
            closeSnackbar(notificationRef.current);
          }

          console.log('ERRRR', graphQLErrors, networkError);
          if (networkError) {
            const context = operation.getContext();
            console.error(`[Network error]: ${networkError}`, context.response?.status || 'no response');
            if (context.response?.status === 503) {
              AppState.setValue(APP_STATES.maintenance);
            }

            // tento retry su problemi di rete
            console.log('Retry on Network Error...');

            enqueueSnackbar(
              t('login.errors.networkError', 'Network error, please try again in a few minutes'),
              {
                variant: 'error',
                autoHideDuration: 5000,
              },
            );

            return forward(operation);
          }

          if (graphQLErrors) {
            graphQLErrors.forEach(({ message, locations, path }) => console.error(
              `[GraphQL error]: Message: ${message}, Path: ${path}`,
              locations,
              message,
            ));

            // tento retry su problemi di GraphQL
            console.log('Retry on GraphQL Error...');

            if (graphQLErrors[0].message === 'Please enter valid credentials') {
              enqueueSnackbar(
                t('login.errors.invalidCredentials', 'Invalid credentials. Please try again.'),
                {
                  variant: 'error',
                  autoHideDuration: 5000,
                },
              );
            } else {
              enqueueSnackbar(
                t('login.errors.graphqlError', 'Error, please try again in a few minutes'),
                {
                  variant: 'error',
                  autoHideDuration: 5000,
                },
              );
            }

            return forward(operation);
          }
        }),
        new RetryLink({
          delay: (count) => count * 1000 * Math.random(),
          attempts: {
            max: 5,
            retryIf: (error) => {
              const validToRetry = error.statusCode !== 400;

              if (validToRetry && !notificationRef.current) {
                notificationRef.current = enqueueSnackbar(
                  t('login.errors.retryWarning', 'Please wait...'),
                  {
                    variant: 'info',
                    persist: true,
                  },
                );
              }

              return validToRetry;
            },
          },
        }),
        new HttpLink({
          uri: CLIENT_SETTINGS.public.gpexeGraphUrl,
        }),
      ]),
    });

    const { data, errors } = await client.mutate<TokenAuthRes, TokenAuthVars>({
      mutation: TOKEN_AUTH,
      variables: {
        email: username,
        password,
      },
      errorPolicy: 'all',
    });

    if (errors) {
      setDialogMessage(t('login.dialog.errorTitle', 'Please enter valid credentials'));
      setIsSending(false);
      return;
    }

    if (data) {
      if (!data.res.isActive) {
        setDialogMessage(t('login.dialog.inactiveAccount', 'This account is inactive'));
        setIsSending(false);
        return;
      }
      await localStorage.setItem('exelio_token', data.res.token);
      await localStorage.setItem('exelio_refresh_token', data.res.refreshToken);
      await localStorage.setItem('exelio_refresh_token_exp', JSON.stringify(data.res.refreshExpiresIn));
    }

    window.location.replace('/');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      login();
    }
  };

  const requestResetPassword = async () => {
    setIsSending(true);
    requestResetUserPasswordMut({
      variables: {
        email: recoverUsername,
      },
    });
  };

  const resetUserPassword = async () => {
    const urlParts = section === 'reset' && window.location.pathname.match(/^\/reset\/(.*)/);
    let resetUrl = urlParts ? urlParts[1] : undefined;

    if (resetUrl && resetUrl[resetUrl.length - 1] === '/') {
      resetUrl = resetUrl.substring(0, resetUrl?.length - 1);
    }

    if (resetUrl) {
      setIsSending(true);
      await updateUserPassword({
        variables: {
          password: resetPassword1,
          resetUrl,
        },
      });
    }
  };
  const handleChangeSection = (sect) => {
    setUsername('');
    setRecoverUsername('');
    setPassword('');
    setSection(sect);
  };

  const loginForm = () => {
    const submitDisabled = isSending
      || username === ''
      || password === ''
      || !validateEmail(username);

    return (
      <Box
        style={{
          width: '100%',
          marginLeft: '50px',
        }}
      >
        {!!dialogMessage && (
        <Typography
          className="login-error"
          variant="subtitle1"
          style={{
            position: 'absolute',
            color: 'red',
            marginTop: '135px',
            marginLeft: '20px',
            width: '80%',
            left: '10%',
          }}
        >
          Please enter valid credentials
        </Typography>
        )}
        <Box
          width="80%"
          onKeyDown={!submitDisabled ? handleKeyPress : undefined}
          style={{
            border: '0.5px solid white',
            borderRadius: '4px',
            padding: '12px 16px',
            marginTop: '175px',
          }}
        >
          <LoginTextField
            label="email"
            fullWidth
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            type="email"
            id="login-email"
            required
            error={!!invalidFields.email}
            value={username}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <UserIcon style={{ width: 30, height: 30, color: 'white' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box
          width="80%"
          onKeyDown={!submitDisabled ? handleKeyPress : undefined}
          style={{
            border: '0.5px solid white',
            borderRadius: '4px',
            padding: '12px 16px',
            marginTop: '20px',
          }}
        >
          <LoginTextField
            label="password"
            fullWidth
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            id="login-password"
            required
            error={!!invalidFields.password}
            value={password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PasswordIcon style={{ width: 30, height: 30, color: 'white' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box {...BoxProperties} mt={2}>
          <Box display="flex" flexDirection="row" alignItems="center" width="80%" justifyContent="space-between">
            <a
              href="#"
              onClick={() => handleChangeSection('recover')}
              className="login-form__links"
              style={{
                fontSize: '0.875rem',
                color: '#ffffff',
                textDecoration: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-main)',
              }}
              onMouseOver={handleLinkHover}
              onMouseOut={handleLinkOut}
            >
              {t('login.lostPassword', 'lost your password?')}
            </a>
            <BaseButton
              onClick={login}
              color="primary"
              isValidation={!submitDisabled}
              disabled={submitDisabled}
              id="login-button"
            >
              {t('login.login', 'login')}
            </BaseButton>
          </Box>
        </Box>
        <WebsiteInfo />
      </Box>
    );
  };

  const recoverForm = () => {
    const submitDisabled = isSending
      || recoverUsername === ''
      || !validateEmail(recoverUsername);

    return (
      <Box
        style={{
          width: '100%',
        }}
      >
        <FormControl error={!!invalidFields.recoverEmail}>
          <Box
            style={{
              width: '100%',
              marginLeft: '50px',
              marginTop: '55px',
            }}
          >
            <Box
              width="80%"
              className="recover-form__intro"
              style={{
                padding: '12px 16px',
                marginTop: '100px',
                fontFamily: 'var(--font-family-alt-main)',
                fontWeight: 700,
              }}
            >
              {t(
                'login.recoverPassword.introText',
                'Enter your email and, if an account with this email exists, we will send you an email with the recovery link.',
              )}
            </Box>
            <Box
              width="80%"
              style={{
                outline: '1px solid rgba(255, 255, 255, 1)',
                borderRadius: '4px',
                padding: '11px 16px',
                marginTop: '20px',
              }}
            >
              <LoginTextField
                fullWidth
                onChange={(e) => setRecoverUsername(e.target.value.toLowerCase())}
                label="email"
                color="#ffffff"
                type="email"
                required
                startAdornment={<EmailRoundedIcon />}
                error={!!invalidFields.recoverEmail}
                value={recoverUsername}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <UserIcon style={{ width: 30, height: 30, color: 'white' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormHelperText>{invalidFields.recoverEmail}</FormHelperText>
            </Box>
            <Box {...BoxProperties} mt={2}>
              <Box display="flex" flexDirection="row" alignItems="center" width="80%" justifyContent="space-between">
                <a
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    handleChangeSection('login');
                  }}
                  className="login-form__links"
                  style={{
                    fontSize: '0.875rem',
                    color: '#ffffff',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-main)',
                  }}
                  onMouseOver={handleLinkHover}
                  onMouseOut={handleLinkOut}
                >
                  {t('login.goToLogin', 'go to login')}
                </a>
                <BaseButton
                  onClick={requestResetPassword}
                  color="primary"
                  isValidation={!submitDisabled}
                  disabled={submitDisabled}
                >
                  {t('login.recover', 'recover')}
                </BaseButton>
              </Box>
            </Box>
          </Box>
        </FormControl>
        <WebsiteInfo />
      </Box>
    );
  };

  const resetForm = () => {
    const submitDisabled = isSending
      || resetPassword1 === ''
      || resetPassword2 === ''
      || resetPassword1 !== resetPassword2;

    const visibilityAdornment = (
      <InputAdornment position="end">
        <IconButton
          className="password-visibility"
          aria-label={t('login.resetPassword.showPassword', 'show password')}
          onClick={() => setShowResetPassword((shown) => !shown)}
          edge="end"
          size="small"
        >
          {showResetPassword
            ? <NoVisibilityIcon style={{ width: 24, height: 24 }} />
            : <VisibilityIcon style={{ width: 24, height: 24 }} />}
        </IconButton>
      </InputAdornment>
    );

    return (
      <Box
        style={{
          width: '100%',
          marginLeft: '50px',
          paddingBottom: '90px',
        }}
      >
        <Box
          width="80%"
          className="recover-form__intro"
          style={{
            padding: '12px 16px',
            marginTop: '110px',
            fontFamily: 'var(--font-family-alt-main)',
            color: '#ffffff',
          }}
        >
          <Box style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '14px' }}>
            {t('login.resetPassword.introText', 'Please enter your new password.')}
          </Box>

          <Box style={{ fontWeight: 400, fontSize: '0.85rem', opacity: 0.85, marginBottom: '6px' }}>
            {t('login.resetPassword.requirementsTitle', 'Your password should not:')}
          </Box>
          <Box
            component="ul"
            style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '0.85rem',
              fontWeight: 400,
              lineHeight: 1.55,
              listStyleType: 'disc',
            }}
          >
            <li>{t('login.resetPassword.req.similar', 'be too similar to your other personal information')}</li>
            <li>{t('login.resetPassword.req.tooShort', 'contain less than 8 characters')}</li>
            <li>{t('login.resetPassword.req.common', 'be a commonly used password')}</li>
            <li>{t('login.resetPassword.req.numeric', 'be entirely numeric')}</li>
            <li>{t('login.resetPassword.req.sameAsOld', 'be the same as the old one')}</li>
          </Box>
        </Box>

        {/* First password field */}
        <Box
          width="80%"
          style={{
            border: '0.5px solid white',
            borderRadius: '4px',
            padding: '12px 16px',
            marginTop: '20px',
          }}
        >
          <LoginTextField
            label="new password"
            fullWidth
            onChange={(e) => setResetPassword1(e.target.value)}
            type={showResetPassword ? 'text' : 'password'}
            id="reset-password-new"
            name="new-password"
            autoComplete="new-password"
            required
            error={!!invalidFields.resetPassword1}
            value={resetPassword1}
            InputProps={{
              endAdornment: visibilityAdornment,
            }}
          />
          <FormHelperText style={{ minHeight: '1.25rem' }}>{invalidFields.resetPassword1}</FormHelperText>
        </Box>

        {/* Repeat password field */}
        <Box
          width="80%"
          style={{
            border: '0.5px solid white',
            borderRadius: '4px',
            padding: '12px 16px',
            marginTop: '20px',
          }}
        >
          <LoginTextField
            label="repeat password"
            fullWidth
            onChange={(e) => setResetPassword2(e.target.value)}
            type={showResetPassword ? 'text' : 'password'}
            id="reset-password-repeat"
            name="new-password-repeat"
            autoComplete="new-password"
            required
            error={!!invalidFields.resetPassword2}
            value={resetPassword2}
            InputProps={{
              endAdornment: visibilityAdornment,
            }}
          />
          <FormHelperText style={{ minHeight: '1.25rem' }}>{invalidFields.resetPassword2}</FormHelperText>
        </Box>

        {/* Buttons section (aligned like loginForm) */}
        <Box {...BoxProperties} mt={2}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            width="80%"
            justifyContent="space-between"
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleChangeSection('login');
              }}
              className="login-form__links"
              style={{
                fontSize: '0.875rem',
                color: '#ffffff',
                textDecoration: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-main)',
                marginLeft: '1.025rem',
              }}
              onMouseOver={handleLinkHover}
              onMouseOut={handleLinkOut}
            >
              {t('login.goToLogin', 'go to login')}
            </a>
            <BaseButton
              onClick={resetUserPassword}
              color="primary"
              isValidation={!submitDisabled}
              disabled={submitDisabled}
              id="reset-button"
            >
              {t('login.activate', 'activate')}
            </BaseButton>
          </Box>
        </Box>
        <WebsiteInfo />
      </Box>
    );
  };

  return (
    <Box
      className="login-form"
      display="flex"
      flexDirection="row"
      justifyContent="flex-start"
      minHeight="100vh"
      width="100%"
      position="relative"
    >
      {/* background images cross-fade */}
      {backgroundImages.map((img, idx) => {
        const isCurrent = idx === currentIndex;
        const isNext = idx === nextIndex;
        return (
          <img
            key={idx}
            src={img}
            alt="background"
            style={{
              ...imageStyle,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              transition: 'opacity 1s ease-in-out',
              opacity: isCurrent ? 1 : isNext ? 0 : 0,
            }}
          />
        );
      })}

      {/* form container */}
      {/* form container with transition */}
      <Box
        flex="1 1 400px"
        minWidth="250px"
        maxWidth="500px"
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        py={3}
        px={2}
        bgcolor="rgba(0, 0, 0, 0.75)"
        color="#ffffff"
        sx={{
          zIndex: 2,
          position: 'relative',
          minHeight: '100vh',
          transform: animateIn ? 'translateX(0)' : 'translateX(-100%)',
          opacity: animateIn ? 1 : 0,
          transition: 'transform 0.8s ease-out, opacity 0.8s ease-out',
        }}
      >
        {/* logo */}
        <Box
          className="xi-logo"
          style={{
            position: 'absolute',
            top: 18,
            left: '1px',
            gap: '8px',
          }}
        >
          <Box
            className="gpexe-logo"
            style={{
              position: 'absolute',
              top: 24,
              left: 64,
              gap: '8px',
            }}
          >
            <GpexeIcon />
          </Box>
        </Box>
        {section === 'login' && loginForm()}
        {section === 'recover' && recoverForm()}
        {section === 'reset' && resetForm()}
      </Box>
    </Box>
  );
}
